import { useState } from "react";
import { Search, ShoppingBag, ArrowRight, ArrowUpRight, Menu, X } from "lucide-react";
import { toast } from "sonner";

const IMG = {
  logo: "https://static.prod-images.emergentagent.com/jobs/76a2a0d3-2a49-42bf-8372-f15f6b4f2234/images/6eb73fffd237ba0167112827429fe70c0b118a6df80d365e3d42d18cec086581.png",
  hero: "https://static.prod-images.emergentagent.com/jobs/76a2a0d3-2a49-42bf-8372-f15f6b4f2234/images/1298d7738acc678822b2fd74aa289a3f30a4ae5f03987bd237a4666f25e2b4dd.png",
  remembrance: "https://static.prod-images.emergentagent.com/jobs/76a2a0d3-2a49-42bf-8372-f15f6b4f2234/images/54d19559d9d339f14992e9c52210bc537d06a66eadc23646d18676ed21f13458.png",
  anniversary: "https://static.prod-images.emergentagent.com/jobs/76a2a0d3-2a49-42bf-8372-f15f6b4f2234/images/0f1635bcc7bb5d02059691ba63323309e7495cd47f82e166ac27f28a5f3f98c6.png",
  heroes: "https://static.prod-images.emergentagent.com/jobs/76a2a0d3-2a49-42bf-8372-f15f6b4f2234/images/7b60ce3bd2d8703890417298129a0cb2c8bc53ed4b66dce76ee6ebe69c88dc69.png",
  homehearth: "https://static.prod-images.emergentagent.com/jobs/76a2a0d3-2a49-42bf-8372-f15f6b4f2234/images/0920d2c7c265f9914ca9f8ccb68a0a8b714350d7560c5d91f916957d8196c652.jpeg",
};

const serif = { fontFamily: "'Playfair Display', 'Cormorant Garamond', serif" };
const sans = { fontFamily: "'Outfit', 'DM Sans', sans-serif" };
const NAV = [
  ["Remembrance", "remembrance"], ["Milestones", "milestones"],
  ["Heroes", "heroes"], ["Home & Hearth", "homehearth"], ["Wholesale", "wholesale"],
];

const Overline = ({ children, className = "" }) => (
  <span className={`text-xs uppercase tracking-[0.2em] font-semibold text-[#C05A45] ${className}`} style={sans}>{children}</span>
);

export default function Honorifica() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [email, setEmail] = useState("");

  const subscribe = (e) => {
    e.preventDefault();
    if (!email.trim()) return toast.error("Enter your email");
    toast.success("Welcome to Honorifica™ — check your inbox for 10% off.");
    setEmail("");
  };

  return (
    <div className="min-h-screen bg-[#FCFBF9] text-[#1C1B19] selection:bg-[#C05A45] selection:text-[#FCFBF9]" style={sans} data-testid="honorifica-home">
      <style>{`
        @keyframes hon-marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        .hon-marquee-track { animation: hon-marquee 26s linear infinite; }
        @keyframes hon-rise { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
        .hon-rise { animation: hon-rise 0.9s cubic-bezier(.2,.7,.2,1) both; }
      `}</style>

      {/* Announcement */}
      <div className="bg-[#768A78] text-[#FCFBF9] text-[11px] uppercase tracking-[0.2em] py-2.5 text-center" style={sans}>
        Personalize any gift · Ships within 48 hours
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#FCFBF9]/80 backdrop-blur-xl border-b border-[#D1C9BB]/60">
        <div className="px-6 md:px-12 lg:px-24 h-20 flex items-center justify-between">
          <button className="lg:hidden text-[#1C1B19]" onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-btn">
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <nav className="hidden lg:flex items-center gap-9 flex-1">
            {NAV.map(([label, id]) => (
              <a key={id} href={`#${id}`} data-testid={`nav-${id}-link`} className="text-sm text-[#3D352E] hover:text-[#C05A45] transition-colors">{label}</a>
            ))}
          </nav>
          <a href="#top" className="lg:absolute lg:left-1/2 lg:-translate-x-1/2 text-2xl tracking-tight" style={serif} data-testid="brand-logo">Honorifica<sup className="text-[0.4em] align-super ml-0.5" data-testid="brand-tm">™</sup></a>
          <div className="flex items-center gap-5 flex-1 justify-end">
            <button aria-label="Search" className="text-[#3D352E] hover:text-[#C05A45] transition-colors"><Search className="w-5 h-5" /></button>
            <button aria-label="Cart" data-testid="cart-btn" className="text-[#3D352E] hover:text-[#C05A45] transition-colors relative">
              <ShoppingBag className="w-5 h-5" />
              <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-[#C05A45] text-[#FCFBF9] text-[9px] flex items-center justify-center">2</span>
            </button>
          </div>
        </div>
        {menuOpen && (
          <div className="lg:hidden border-t border-[#D1C9BB]/60 px-6 py-4 flex flex-col gap-3">
            {NAV.map(([label, id]) => <a key={id} href={`#${id}`} className="text-sm text-[#3D352E]" onClick={() => setMenuOpen(false)}>{label}</a>)}
          </div>
        )}
      </header>

      {/* Hero */}
      <section id="top" className="px-6 md:px-12 lg:px-24 pt-16 lg:pt-24 pb-20 lg:pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          <div className="lg:col-span-5 hon-rise">
            <Overline>Gifts that honor</Overline>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight" style={serif}>
              Gifts that speak what the heart feels.
            </h1>
            <p className="mt-6 text-lg text-[#3D352E] leading-relaxed max-w-md">
              Keepsakes made to remember, to celebrate, and to honor the people who matter most — thoughtfully personalized, made with care.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <a href="#collections" data-testid="shop-collections-hero-btn" className="bg-[#C05A45] text-white hover:bg-[#A64B38] px-8 py-4 transition-transform hover:-translate-y-1 font-medium inline-flex items-center gap-2">
                Shop the Collections <ArrowRight className="w-4 h-4" />
              </a>
              <a href="#personalize" className="border border-[#1C1B19] text-[#1C1B19] hover:bg-[#1C1B19] hover:text-[#FCFBF9] px-8 py-4 transition-colors font-medium">
                How personalization works
              </a>
            </div>
          </div>
          <div className="lg:col-span-7 relative">
            <div className="overflow-hidden">
              <img src={IMG.hero} alt="Honorifica™ keepsakes on a mantel" className="w-full h-[380px] lg:h-[520px] object-cover" />
            </div>
            <div className="hidden sm:block absolute -bottom-8 -left-6 w-44 lg:w-56 overflow-hidden border-8 border-[#FCFBF9] shadow-sm">
              <img src={IMG.remembrance} alt="Keepsake mug" className="w-full h-40 lg:h-52 object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Marquee ribbon */}
      <div className="bg-[#F4EFE6] border-y border-[#D1C9BB] py-6 overflow-hidden">
        <div className="hon-marquee-track whitespace-nowrap flex" style={serif}>
          {[0, 1].map((k) => (
            <span key={k} className="text-xl text-[#3D352E] flex items-center shrink-0">
              {["Meaningful", "Timeless", "Made with Care", "Honorifica™", "Remembered Always"].map((w, i) => (
                <span key={i} className="flex items-center">
                  <span className="px-8">{w}</span>
                  <span className="text-[#C05A45]">✦</span>
                </span>
              ))}
            </span>
          ))}
        </div>
      </div>

      {/* Featured collections — bento */}
      <section id="collections" className="px-6 md:px-12 lg:px-24 py-20 lg:py-28">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-12">
          <div>
            <Overline>Shop by feeling</Overline>
            <h2 className="mt-3 text-3xl sm:text-4xl tracking-tight" style={serif}>Collections for life's deepest moments</h2>
          </div>
          <a href="#collections" className="text-sm text-[#3D352E] hover:text-[#C05A45] inline-flex items-center gap-1.5 self-start sm:self-auto">View all <ArrowRight className="w-4 h-4" /></a>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 lg:gap-8">
          <CollectionCard id="remembrance" tall img={IMG.remembrance} eyebrow="Remembrance & Memorial" title="Keep them close, always" copy="Pet memorials and tributes to loved ones, made to comfort." />
          <div className="md:col-span-6 grid grid-rows-2 gap-6 lg:gap-8">
            <CollectionCard id="milestones" img={IMG.anniversary} eyebrow="Anniversaries & Milestones" title="Celebrate the years" copy="First anniversaries, weddings & the milestones between." />
            <CollectionCard id="heroes" img={IMG.heroes} eyebrow="Everyday Heroes" title="Honor those who serve" copy="For veterans, first responders, nurses & teachers." />
          </div>
          <div className="md:col-span-12">
            <CollectionCard id="homehearth" img={IMG.homehearth} eyebrow="Home & Hearth" title="Where memories gather" copy="Coasters, placemats & keepsakes for the home — housewarmings, family tables & everyday togetherness." />
          </div>
        </div>
      </section>

      {/* Personalization value */}
      <section id="personalize" className="bg-[#E2DCD0] py-20 lg:py-28">
        <div className="px-6 md:px-12 lg:px-24 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="relative">
            <img src={IMG.anniversary} alt="Personalized set" className="w-full h-[340px] lg:h-[440px] object-cover" />
            <img src={IMG.remembrance} alt="Personalized mug" className="hidden sm:block absolute -bottom-10 -right-8 w-40 lg:w-52 h-40 lg:h-52 object-cover border-8 border-[#E2DCD0]" />
          </div>
          <div>
            <Overline>Made with care</Overline>
            <h2 className="mt-3 text-3xl sm:text-4xl tracking-tight leading-tight" style={serif}>Add the name, the date, the little detail that means everything.</h2>
            <p className="mt-5 text-[#3D352E] leading-relaxed max-w-lg">
              Every Honorifica™ piece can be personalized after you order — a name, a date, a photo, or a few heartfelt words. We proof it, print it, and ship it fast so your gift arrives right on time.
            </p>
            <ul className="mt-7 space-y-3">
              {["Free personalization on every design", "Design proof before we print", "Ships within 48 hours"].map((t) => (
                <li key={t} className="flex items-center gap-3 text-[#1C1B19]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C05A45]" /> {t}
                </li>
              ))}
            </ul>
            <a href="#collections" data-testid="personalize-cta-btn" className="mt-9 inline-flex items-center gap-2 bg-[#C05A45] text-white hover:bg-[#A64B38] px-8 py-4 transition-transform hover:-translate-y-1 font-medium">
              Start your keepsake <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Social proof */}
      <section className="px-6 md:px-12 lg:px-24 py-24 lg:py-32 text-center">
        <div className="text-6xl text-[#C05A45] leading-none" style={serif}>&ldquo;</div>
        <blockquote className="max-w-3xl mx-auto -mt-4 text-2xl sm:text-3xl leading-snug tracking-tight text-[#1C1B19]" style={serif}>
          I ordered a memorial mug for my mom after we lost my father. She cried the moment she opened it. It's the most meaningful gift I've ever given.
        </blockquote>
        <div className="mt-8">
          <Overline>Danielle R. · Verified Buyer</Overline>
        </div>
      </section>

      {/* Wholesale */}
      <section id="wholesale" className="bg-[#1C1B19] text-[#FCFBF9]">
        <div className="px-6 md:px-12 lg:px-24 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <Overline className="text-[#C7A79C]">For boutiques & stockists</Overline>
            <h2 className="mt-3 text-3xl sm:text-4xl tracking-tight" style={serif}>Stock Honorifica™ in your shop.</h2>
            <p className="mt-5 text-[#D1C9BB] leading-relaxed max-w-lg">
              Curated, emotionally-driven keepsakes your customers will treasure. Wholesale pricing, low minimums, and fast reorders — available now on Faire.
            </p>
          </div>
          <div className="lg:justify-self-end">
            <a href="https://honorifica.faire.com" target="_blank" rel="noreferrer" data-testid="wholesale-faire-btn" className="inline-flex items-center gap-2 border border-[#FCFBF9] text-[#FCFBF9] hover:bg-[#FCFBF9] hover:text-[#1C1B19] px-8 py-4 transition-colors font-medium">
              Shop wholesale on Faire <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#F4EFE6] text-[#1C1B19] pt-20 pb-8">
        <div className="px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 pb-16 border-b border-[#D1C9BB]">
            <div>
              <Overline>Join the family</Overline>
              <h3 className="mt-3 text-2xl sm:text-3xl tracking-tight max-w-md" style={serif}>Get 10% off your first keepsake.</h3>
              <form onSubmit={subscribe} className="mt-6 flex max-w-md" data-testid="newsletter-form">
                <input
                  type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@email.com" data-testid="newsletter-input"
                  className="flex-1 bg-transparent border border-[#D1C9BB] border-r-0 px-4 py-3.5 text-sm outline-none focus:border-[#C05A45] placeholder:text-[#7A7268]"
                />
                <button type="submit" data-testid="newsletter-submit" className="bg-[#1C1B19] text-[#FCFBF9] hover:bg-[#C05A45] px-6 transition-colors text-sm font-medium">Subscribe</button>
              </form>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-8 lg:justify-items-end">
              {[["Shop", ["Remembrance", "Milestones", "Heroes", "Home & Hearth", "Best Sellers"]],
                ["Support", ["Personalization", "Shipping", "Returns", "Contact"]],
                ["Company", ["Our Story", "Wholesale", "Reviews", "Gift Cards"]]].map(([h, items]) => (
                <div key={h}>
                  <div className="text-sm font-semibold mb-3">{h}</div>
                  <ul className="space-y-2">
                    {items.map((i) => <li key={i}><a href="#collections" className="text-sm text-[#7A7268] hover:text-[#C05A45] transition-colors">{i}</a></li>)}
                  </ul>
                </div>
              ))}
            </div>
          </div>
          <div className="text-[13vw] lg:text-[11vw] leading-none tracking-tight text-[#1C1B19]/90 pt-10 select-none" style={serif}>Honorifica<sup className="text-[0.3em] align-super">™</sup></div>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-[#7A7268]">
            <span>© {new Date().getFullYear()} Honorifica™. Honorifica™ is a trademark of Honorifica. Made with care.</span>
            <span>Remembrance · Milestones · Everyday Heroes</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

function CollectionCard({ id, img, eyebrow, title, copy, tall }) {
  return (
    <a href="#collections" data-testid={`collection-${id}`}
      className={`group relative overflow-hidden block ${tall ? "md:col-span-6 min-h-[340px] md:min-h-full" : "min-h-[240px]"}`}>
      <img src={img} alt={title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#1C1B19]/75 via-[#1C1B19]/10 to-transparent" />
      <div className="relative h-full flex flex-col justify-end p-7 lg:p-9 text-[#FCFBF9]">
        <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#F2D8D3]" style={sans}>{eyebrow}</span>
        <h3 className="mt-2 text-2xl lg:text-3xl tracking-tight" style={serif}>{title}</h3>
        <p className="mt-2 text-sm text-[#E2DCD0] max-w-xs leading-relaxed">{copy}</p>
        <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium">Shop now <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" /></span>
      </div>
    </a>
  );
}
